/**
 * The unit sheet's **BUILD** adapters (`decisions/0206`): the roster build — a `PilotData`, its
 * `ComputedMech` / `ComputedPilot` and the compendium — turned into the same stat cells and loadout
 * rows the combat sheet's **WEAR** derivations (`descriptiveStatCells` over the frozen base,
 * {@link mechLoadout}, {@link pilotLoadout}) produce from a spawned `Combatant`. The PILOT and MECH
 * pages render the combat sheet's parts from these.
 *
 * The two sides cannot drift: the rows come from the same builders (`mechWeaponRow`,
 * `mechSystemRow`, `mechFeatureRows`, `pilotWeaponRow`, `pilotGearRows`), the weapon and system
 * profiles from the same freezes the spawn adapters call (`freezeMechWeapons`, `freezeSystems`,
 * `freezePilotGearWeapons`, `freezePilotEffectIds`), and `unitBuildView.parity.test.ts` pins that a
 * build and its spawned unit give the same cells and rows. What BUILD adds is what a frozen base
 * drops: the mount layout with its empty and locked slots, mods as sub-rows, the SP budget, and a
 * mech with no frame (identity and empty mounts, no stats).
 */
import type { Compendium } from "@lancer-vtt/compendium";
import {
  composedMountProvenance,
  composedMounts,
  computeSpMax,
  computeSpUsed,
  flexSlot1Locked,
  GMS_LICENSE_ID,
  loadoutViolations,
  resolvePrintedMechValues,
  slotSizesForMount,
  triggerBonus,
  weaponInstanceKey,
  type LoadoutViolation,
  type MechData,
  type PilotData,
} from "@lancer-vtt/domain";
import { freezeMechWeapons, freezePilotEffectIds, freezePilotGearWeapons, freezeSystems, type ComputedMech, type ComputedPilot } from "@lancer-vtt/rules";
import { licenseSlug, sortByName } from "../editor";
import { FICTION_ONLY_LABEL, FICTION_ONLY_TITLE } from "../fictionOnly";
import { descriptiveStatCells, haseStatCells, type StatCell } from "./combatWindows/unitStats";
import { mechFeatureRows, mechSystemRow, mechWeaponRow, pilotGearRows, pilotWeaponRow, type LoadoutChip, type LoadoutGroup, type LoadoutRow } from "./unitLoadout";

/** Whether an entry is fiction-only in this client (`useFictionOnly`); the build rows mark it with a chip. */
export type FictionOnly = (entryId: string) => boolean;

export const FICTION_ONLY_CHIP: LoadoutChip = { label: FICTION_ONLY_LABEL.toUpperCase(), tone: "fiction", title: FICTION_ONLY_TITLE };

const ROMAN = ["", "I", "II", "III", "IV", "V", "VI"];

/** A track's carried value over its max: the BUILD vitals show maxima, and the owner's wear between fights. */
export type BuildTrack = { value: number; max: number };

export type MechBuildView = {
  /** Absent for a mech with no resolved frame: it has no stats. */
  stats?: StatCell[];
  hase: StatCell[];
  vitals?: { hp: BuildTrack; heat: BuildTrack; structure: BuildTrack; stress: BuildTrack; repair: BuildTrack };
  groups: LoadoutGroup[];
  sp: { used: number; max: number };
  violations: LoadoutViolation[];
};

/**
 * A mech's BUILD view. `wear` true reads its carried wear off `printedMechs` (the owner's and the
 * GM's page); false shows maxima only (another player's view, where `printedMechs` is hidden).
 */
export function mechBuildView(args: {
  pilot: PilotData;
  mech: MechData;
  computed: ComputedMech | undefined;
  grit: number;
  compendium: Compendium;
  wear: boolean;
  fictionOnly?: FictionOnly;
}): MechBuildView {
  const { pilot, mech, computed, compendium } = args;
  const fictionOnly = args.fictionOnly ?? (() => false);
  const frame = mech.frameId ? compendium.getById("frame", mech.frameId) : undefined;
  const hase = haseStatCells({ hull: pilot.mechSkills.hull, agility: pilot.mechSkills.agi, systems: pilot.mechSkills.sys, engineering: pilot.mechSkills.eng });

  let stats: StatCell[] | undefined;
  let vitals: MechBuildView["vitals"];
  if (computed) {
    const s = computed.stats;
    // Armor, structure and stress are flat frame stats `compute` does not fold — read as `spawnMech` reads them.
    const armor = typeof frame?.stats.armor === "number" ? frame.stats.armor : 0;
    stats = descriptiveStatCells(
      { evasion: s.evasion, edef: s.edef, speed: s.speed, armor, size: s.size, sensors: s.sensorRange, saveTarget: s.save, attackBonus: args.grit },
      { grit: true },
    );
    const maxStructure = typeof frame?.stats.structure === "number" ? frame.stats.structure : 4;
    const maxStress = typeof frame?.stats.stress === "number" ? frame.stats.stress : 4;
    const carried = resolvePrintedMechValues(args.wear ? pilot.printedMechs?.[mech.id] : undefined, { maxHp: s.hp, maxStructure, maxStress, repairCapMax: s.repcap });
    vitals = {
      hp: { value: carried.hp, max: s.hp },
      heat: { value: 0, max: s.heatcap },
      structure: { value: carried.structure, max: maxStructure },
      stress: { value: carried.stress, max: maxStress },
      repair: { value: carried.repairsRemaining, max: s.repcap },
    };
  }

  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  const weapons = freezeMechWeapons({ pilotData: pilot, mech, compendium });
  const byKey = new Map(weapons.map((w) => [weaponInstanceKey(w), w]));
  const weaponRows: LoadoutRow[] = [];
  if (loadout && mech.frameId) {
    const mounts = composedMounts(loadout, pilot, compendium);
    const provenance = composedMountProvenance(loadout, pilot, compendium);
    mounts.forEach((mount, mi) => {
      const prov = provenance[mi] ?? null;
      const section =
        prov?.storageField === "integratedWeaponMount" ? "INTEGRATED WEAPON MOUNT" : `${mount.type.toUpperCase()} MOUNT${prov ? ` · BONUS${prov.noMods ? " · NO MODS" : ""}` : ""}`;
      const sizes = slotSizesForMount(mount.type) ?? [];
      const slot1Locked = flexSlot1Locked(mount, compendium);
      mount.slots.forEach((slot, si) => {
        const key = `${mi}:${si}`;
        const size = (sizes[si] ?? mount.type).toUpperCase();
        if (!slot.weaponId) {
          weaponRows.push(
            si === 1 && slot1Locked
              ? { key, kind: "AUX", name: "Locked: this Flex mount holds a Main weapon", chips: [], section, variant: "locked" }
              : { key, kind: size.slice(0, 4), name: `Empty ${size.toLowerCase()} slot`, chips: [], section, variant: "empty" },
          );
          return;
        }
        const profile = byKey.get(key);
        const row: LoadoutRow = profile
          ? mechWeaponRow(profile, compendium)
          : { key, kind: "WPN", name: slot.weaponId, chips: [] };
        weaponRows.push({ ...row, section, ref: { type: "weapon", id: slot.weaponId }, ...fictionChip(row, slot.weaponId, fictionOnly) });
        if (slot.modId) {
          const mod = compendium.getById("mod", slot.modId);
          weaponRows.push({
            key: `${key}:mod`,
            kind: "MOD",
            name: mod?.name ?? slot.modId,
            ...(typeof mod?.sp === "number" ? { meta: `${mod.sp} SP` } : {}),
            chips: [],
            ...(typeof mod?.effect === "string" && mod.effect.trim() ? { text: mod.effect } : {}),
            section,
            variant: "sub",
            ref: { type: "mod", id: slot.modId },
          });
        }
      });
    });
  }
  // Frame- and talent-integrated weapons ride no mount: they close the list, as the fight lists them.
  for (const w of weapons) {
    if (!(w.instanceId ?? "").startsWith("integrated:")) continue;
    weaponRows.push({ ...mechWeaponRow(w, compendium), section: "INTEGRATED", ref: { type: "weapon", id: w.id } });
  }

  const systemRows = mech.frameId
    ? freezeSystems(mech, pilot, compendium).map((s) => {
        const row = mechSystemRow(s, compendium);
        return { ...row, ref: { type: "system" as const, id: s.id }, ...fictionChip(row, s.id, fictionOnly) };
      })
    : [];
  const features = mech.frameId ? mechFeatureRows({ frameId: mech.frameId, pilotEffectIds: freezePilotEffectIds(pilot), compendium }) : [];

  return {
    stats,
    hase,
    vitals,
    groups: [
      { id: "weapons", title: "WEAPONS", rows: weaponRows },
      { id: "systems", title: "SYSTEMS", rows: systemRows },
      { id: "core", title: "CORE · TALENTS · TRAITS", rows: features },
    ].filter((g) => g.rows.length > 0),
    sp: { used: computeSpUsed(mech, pilot, compendium), max: computeSpMax(mech, pilot, compendium) },
    violations: loadoutViolations(mech, pilot, compendium),
  };
}

function fictionChip(row: LoadoutRow, entryId: string, fictionOnly: FictionOnly): Pick<LoadoutRow, "chips"> {
  return { chips: fictionOnly(entryId) ? [...row.chips, FICTION_ONLY_CHIP] : row.chips };
}

export type PilotBuildView = {
  /** Absent until the roster's computed view has the pilot. */
  stats?: StatCell[];
  hase: StatCell[];
  hp?: BuildTrack;
  /** The on-foot kit: WEAPONS, ARMOR, GEAR. */
  loadout: LoadoutGroup[];
  /** The build sections the combat sheet has no counterpart for: SKILL TRIGGERS, TALENTS, LICENSES, CORE BONUSES. */
  build: LoadoutGroup[];
};

/**
 * A pilot's BUILD view: the on-foot stats and kit as {@link pilotLoadout} gives them in a fight, plus
 * the build-only sections. `wear` true reads the carried on-foot HP (`currentHp`); false shows the max.
 */
export function pilotBuildView(args: { pilot: PilotData; computed: ComputedPilot | undefined; compendium: Compendium; wear: boolean; fictionOnly?: FictionOnly }): PilotBuildView {
  const { pilot, computed, compendium } = args;
  const fictionOnly = args.fictionOnly ?? (() => false);
  const c = computed?.combat;
  const stats = c
    ? descriptiveStatCells(
        { evasion: c.evasion, edef: c.edef, speed: c.speed, armor: c.armor, size: c.size, sensors: c.sensorRange, saveTarget: c.save, attackBonus: c.attackBonus },
        { grit: true },
      )
    : undefined;
  const hp = c ? { value: args.wear ? Math.max(0, Math.min(pilot.currentHp ?? c.hp, c.hp)) : c.hp, max: c.hp } : undefined;

  const weapons = freezePilotGearWeapons(pilot, compendium).map((w) => {
    const row = pilotWeaponRow(w, compendium);
    return { ...row, ref: { type: "pilot_gear" as const, id: w.id }, ...fictionChip(row, w.id, fictionOnly) };
  });
  const { armor, gear } = pilotGearRows({
    gearIds: pilot.gear,
    compendium,
    extend: (id) => ({ ref: { type: "pilot_gear", id }, chips: fictionOnly(id) ? [FICTION_ONLY_CHIP] : [] }),
  });

  const triggers: LoadoutRow[] = Object.entries(pilot.skills)
    .filter(([, rank]) => rank > 0)
    .map(([id, rank]) => {
      const item = compendium.getById("skill", id);
      const name = item?.name ?? id;
      const bonus = triggerBonus(rank);
      const text = [item?.description, item?.detail].filter((t): t is string => typeof t === "string" && t.trim() !== "").join("<br>");
      return { key: id, kind: "TRG", name, meta: `+${bonus}`, chips: [], ...(text ? { text } : {}), ref: { type: "skill", id }, roll: { label: name, bonus } };
    });

  const talents: LoadoutRow[] = Object.entries(pilot.talents)
    .filter(([, rank]) => rank > 0)
    .map(([id, rank]) => {
      const item = compendium.getById("talent", id);
      const ranks = item?.ranks ?? [];
      const text = ranks
        .slice(0, rank)
        .map((r) => (typeof r.description === "string" && r.description.trim() ? `<b>${r.name.toUpperCase()}</b> ${r.description}` : ""))
        .filter((t) => t !== "")
        .join("<br>");
      return {
        key: id,
        kind: "TAL",
        name: item?.name ?? id,
        meta: `RANK ${ROMAN[rank] ?? rank}`,
        chips: fictionOnly(`${id}:${rank}`) ? [FICTION_ONLY_CHIP] : [],
        ...(text ? { text } : {}),
        ref: { type: "talent", id },
      };
    });

  // A license's name is its frame's, by the slug the editor and reducer derive (frame name → license id).
  const licenseNames = new Map<string, string>();
  for (const f of compendium.list("frame")) {
    const name = f.source === "GMS" ? "GMS" : f.name;
    if (!name) continue;
    const id = licenseSlug(name);
    if (!licenseNames.has(id)) licenseNames.set(id, name);
  }
  const licenses: LoadoutRow[] = sortByName(
    pilot.licenses.filter((l) => l.id !== GMS_LICENSE_ID && l.rank > 0).map((l) => ({ id: l.id, rank: l.rank, name: licenseNames.get(l.id) ?? l.id })),
  ).map((l) => ({ key: l.id, kind: "LIC", name: l.name, meta: `RANK ${ROMAN[l.rank] ?? l.rank}`, chips: [], ref: { type: "license", id: l.id } }));

  const coreBonuses: LoadoutRow[] = pilot.coreBonuses.map((id) => {
    const cb = compendium.getById("core_bonus", id);
    const effect = typeof cb?.effect === "string" && cb.effect.trim() ? cb.effect : undefined;
    return {
      key: id,
      kind: "CB",
      name: cb?.name ?? id,
      ...(cb?.source ? { meta: String(cb.source) } : {}),
      chips: fictionOnly(id) ? [FICTION_ONLY_CHIP] : [],
      ...(effect ? { text: effect } : {}),
      ref: { type: "core_bonus", id },
    };
  });

  return {
    stats,
    hase: haseStatCells({ hull: pilot.mechSkills.hull, agility: pilot.mechSkills.agi, systems: pilot.mechSkills.sys, engineering: pilot.mechSkills.eng }),
    hp,
    loadout: [
      { id: "weapons", title: "WEAPONS", rows: weapons },
      { id: "armor", title: "ARMOR", rows: armor },
      { id: "gear", title: "GEAR", rows: gear },
    ].filter((g) => g.rows.length > 0),
    build: [
      { id: "triggers", title: "SKILL TRIGGERS", rows: triggers },
      { id: "talents", title: "TALENTS", rows: talents },
      { id: "licenses", title: "LICENSES", rows: licenses },
      { id: "core-bonuses", title: "CORE BONUSES", rows: coreBonuses },
    ],
  };
}
